import { memo, useEffect } from 'react';
import {
  withTiming,
  useSharedValue,
  useAnimatedStyle,
  withDelay,
  Easing,
} from 'react-native-reanimated';
import { useSelector } from '@bonanza-social/toolkit/state';
import {
  Box,
  Text,
  Image,
  Button,
  Breakpoint,
  useBreakpoint,
  AnimationPlayer,
} from '@bonanza-social/moonlight';
import { selectPrizeWallPrize } from '../../state/prizeWall/prizeWall.slice';
import { PrizeWallAwardProps } from './PrizeWallAward.types';
import PrizeWallGlow from '../PrizeWallGlow/PrizeWallGlow';
import animations from '../../assets/animations';

function PrizeWallAward({
  rewardId,
  glowX,
  glowY,
  onButtonPress,
}: PrizeWallAwardProps) {
  const { name, type, imageUrl } =
    useSelector(selectPrizeWallPrize(rewardId)) || {};
  const breakpoint = useBreakpoint();
  const animatedValue = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: animatedValue.value,
  }));
  const glowAnimationDuration = 300;

  useEffect(() => {
    animatedValue.value = withDelay(
      glowAnimationDuration,
      withTiming(1, { easing: Easing.linear }),
    );
  }, [glowAnimationDuration, animatedValue]);

  return (
    <Box flex={1}>
      <Box
        position="absolute"
        top="space-0"
        left="space-0"
        transform={`translate(${glowX - 25}px, ${glowY - 25}px)`}
      >
        <PrizeWallGlow
          width={50}
          height={50}
          transform={`translate(${glowX}px, ${glowY}px)`}
          animationDuration={glowAnimationDuration}
        />
      </Box>
      <Box
        paddingHorizontal="space-600"
        paddingBottom="safe-area-inset-bottom"
        justifyContent="flex-end"
        animatedStyle={animatedStyle}
        flex={1}
      >
        <Image
          name={`prizeWall.awardBackground${breakpoint === Breakpoint.MOBILE ? 'Mobile' : 'Desktop'}`}
          contentFit="cover"
          position="absolute"
          inset={0}
        />
        <Box width={327} height={184} centerContent alignSelf="center">
          <Box
            position="absolute"
            transform="translateY(-184px)"
            width={447}
            height={464}
          >
            <AnimationPlayer
              source={animations.goldenConfetti}
              sizeX="100%"
              sizeY="100%"
            />
          </Box>
          <Image
            name="prizeWall.sunRays"
            position="absolute"
            width={{ mobile: 500, tablet: 800, desktop: 800 }}
            height={{ mobile: 500, tablet: 800, desktop: 800 }}
          />
          <Image source={imageUrl} width="100%" height="100%" />
        </Box>
        <Text
          variant="body-l"
          locNS="prizeWall"
          locKey="prizeWallAwardTitle"
          paddingTop="space-600"
          paddingBottom="space-100"
          textAlign="center"
        />
        <Text variant="h2" textAlign="center" paddingBottom="space-1200">
          {name}
        </Text>
        {type === 'Physical' && (
          <Text
            variant="body-s"
            locNS="prizeWall"
            locKey="prizeWallAwardDescription"
            paddingBottom="space-400"
            textAlign="center"
          />
        )}
        <Button
          locNS="common"
          locKey="continue"
          marginBottom="space-800"
          alignSelf="center"
          width={{
            mobile: '100%',
            tablet: 375,
            desktop: 375,
          }}
          onPress={onButtonPress}
          light
        />
      </Box>
    </Box>
  );
}

export default memo(PrizeWallAward);
